import { useEffect, useRef, useState, useCallback } from "react"
import { DashboardLayout } from "../../../components/dashboard-layout"
import { Button } from "../../../components/ui/button"
import { Textarea } from "../../../components/ui/textarea"
import { Card, CardHeader, CardTitle } from "../../../components/ui/card"
import { Badge } from "../../../components/ui/badge"
import {
  Send,
  Sparkles,
  Loader2,
  MessageSquare,
  Plus,
  Trash2,
  PanelLeftClose,
  PanelLeft,
  Paperclip,
  Download,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  HelpCircle,
} from "lucide-react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { useTheme } from "../../../contexts/theme-provider"
import { cn } from "../../../lib/utils"
import { motion, AnimatePresence } from "framer-motion"
import {
  streamPolicyChat,
  getChatSessions,
  deleteChatSession,
  getSessionMessages,
  checkCompliance,
  downloadPolicyDocument,
  type ChatStreamEvent,
  type ChatSession,
  type ComplianceResult,
  type ComplianceViolation,
  type SourceReference,
} from "../../../services/policySearch"

interface ChatBubble {
  role: "user" | "assistant"
  content: string
  streaming?: boolean
  /** Structured compliance-check result (renders a card, not markdown) */
  compliance?: ComplianceResult
  /** Policy files/pages the answer was grounded on (downloadable) */
  references?: SourceReference[]
}

const EMPTY_STATE_QUERIES = [
  "What policies are available?",
  "Summarise the policies",
  "What are the collateral requirements?",
]

/** Structured, tabular rendering of a compliance-check result. */
function ComplianceResultCard({
  r,
  isDark,
  disabled,
  onAsk,
}: {
  r: ComplianceResult
  isDark: boolean
  disabled: boolean
  onAsk: (q: string) => void
}) {
  const [dl, setDl] = useState<number | null>(null)
  const [dlErr, setDlErr] = useState("")

  async function handleDownload(v: ComplianceViolation) {
    if (!v.document_id || dl !== null) return
    setDl(v.document_id)
    setDlErr("")
    try {
      await downloadPolicyDocument(v.document_id, v.document_name)
    } catch {
      setDlErr("Could not download the reference file.")
    } finally {
      setDl(null)
    }
  }

  const tone =
    r.compliant === true
      ? {
          icon: CheckCircle2,
          ring: "!border-emerald-300 dark:!border-emerald-700",
          bg: "!bg-emerald-50 dark:!bg-emerald-950/40",
          text: "!text-emerald-700 dark:!text-emerald-300",
          label: "Compliant",
        }
      : r.compliant === false
      ? {
          icon: XCircle,
          ring: "!border-red-300 dark:!border-red-800",
          bg: "!bg-red-50 dark:!bg-red-950/40",
          text: "!text-red-700 dark:!text-red-300",
          label: "Not compliant",
        }
      : {
          icon: AlertTriangle,
          ring: "!border-amber-300 dark:!border-amber-700",
          bg: "!bg-amber-50 dark:!bg-amber-950/40",
          text: "!text-amber-700 dark:!text-amber-300",
          label: "Undetermined",
        }
  const Icon = tone.icon

  const th = cn(
    "!px-3 !py-2 !text-left !text-[11px] !font-semibold !uppercase !tracking-wide !border-b",
    isDark
      ? "!border-slate-600 !text-slate-300 !bg-slate-800/80"
      : "!border-slate-300 !text-slate-600 !bg-slate-200/60 dark:!border-slate-600 dark:!text-slate-300 dark:!bg-slate-800/80"
  )
  const td = cn(
    "!px-3 !py-2 !align-top !border-b !text-xs",
    isDark
      ? "!border-slate-700 !text-slate-200"
      : "!border-slate-200 !text-slate-700 dark:!border-slate-700 dark:!text-slate-200"
  )

  return (
    <div className="!w-full !space-y-3">
      {/* Verdict banner */}
      <div
        className={cn(
          "!flex !items-start !gap-2.5 !rounded-xl !border !px-3 !py-2.5",
          tone.ring,
          tone.bg
        )}
      >
        <Icon className={cn("!h-5 !w-5 !mt-0.5 !flex-shrink-0", tone.text)} />
        <div className="!min-w-0">
          <div className={cn("!text-sm !font-semibold", tone.text)}>
            {tone.label} — {r.file}
          </div>
          <div
            className={cn(
              "!text-xs !mt-0.5",
              isDark ? "!text-slate-300" : "!text-slate-600 dark:!text-slate-300"
            )}
          >
            {r.summary}
          </div>
          <div
            className={cn(
              "!text-[11px] !mt-1",
              isDark ? "!text-slate-400" : "!text-slate-500 dark:!text-slate-400"
            )}
          >
            {r.sections_checked} section{r.sections_checked !== 1 ? "s" : ""} checked
            {r.truncated && " (document truncated — partial check)"}
            {r.policies_checked.length > 0 &&
              ` · against ${r.policies_checked.join(", ")}`}
          </div>
        </div>
      </div>

      {/* Violations table */}
      {r.violations.length > 0 && (
        <div
          className={cn(
            "!rounded-xl !border !overflow-x-auto",
            isDark ? "!border-slate-700" : "!border-slate-200 dark:!border-slate-700"
          )}
        >
          <table className="!min-w-full !border-collapse !text-left">
            <thead>
              <tr>
                <th className={th}>Policy</th>
                <th className={th}>Breached rule</th>
                <th className={th}>Detail</th>
                <th className={th}>Reference</th>
              </tr>
            </thead>
            <tbody>
              {r.violations.map((v, i) => (
                <tr
                  key={i}
                  className={cn(
                    isDark ? "hover:!bg-slate-800/50" : "hover:!bg-slate-50 dark:hover:!bg-slate-800/50"
                  )}
                >
                  <td className={cn(td, "!font-medium !whitespace-normal")}>
                    {v.policy || "—"}
                  </td>
                  <td className={cn(td, "!whitespace-normal")}>{v.rule || "—"}</td>
                  <td className={cn(td, "!whitespace-normal")}>
                    {v.explanation || "—"}
                    {(v.section || v.document_excerpt) && (
                      <div
                        className={cn(
                          "!mt-1 !text-[11px] !italic",
                          isDark ? "!text-slate-400" : "!text-slate-500 dark:!text-slate-400"
                        )}
                      >
                        {v.section}
                        {v.section && v.document_excerpt && " — "}
                        {v.document_excerpt && `"${v.document_excerpt}"`}
                      </div>
                    )}
                  </td>
                  <td className={cn(td, "!whitespace-nowrap")}>
                    {v.document_id ? (
                      <button
                        type="button"
                        onClick={() => handleDownload(v)}
                        disabled={dl !== null}
                        className={cn(
                          "!inline-flex !items-center !gap-1.5 !rounded-md !border !px-2 !py-1 !text-[11px] !font-medium !transition-colors",
                          isDark
                            ? "!border-blue-800 !text-blue-300 hover:!bg-blue-950"
                            : "!border-blue-200 !text-blue-600 hover:!bg-blue-50 dark:!border-blue-800 dark:!text-blue-300 dark:hover:!bg-blue-950"
                        )}
                        title={v.document_name || "Download policy file"}
                      >
                        {dl === v.document_id ? (
                          <Loader2 className="!h-3 !w-3 !animate-spin" />
                        ) : (
                          <Download className="!h-3 !w-3" />
                        )}
                        {v.document_name || "Policy file"}
                        {v.page && ` · p. ${v.page}`}
                      </button>
                    ) : (
                      <span
                        className={cn(
                          "!text-[11px]",
                          isDark ? "!text-slate-400" : "!text-slate-500 dark:!text-slate-400"
                        )}
                      >
                        {v.policy_reference || "—"}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dlErr && (
        <div className="!text-[11px] !text-red-500">{dlErr}</div>
      )}

      {/* Suggested follow-up questions */}
      {r.suggested_questions.length > 0 && (
        <div className="!space-y-1.5">
          <div
            className={cn(
              "!flex !items-center !gap-1.5 !text-[11px] !font-medium !uppercase !tracking-wide",
              isDark ? "!text-slate-400" : "!text-slate-500 dark:!text-slate-400"
            )}
          >
            <HelpCircle className="!h-3.5 !w-3.5" />
            Ask next
          </div>
          <div className="!flex !flex-wrap !gap-1.5">
            {r.suggested_questions.map((q, i) => (
              <button
                key={i}
                type="button"
                onClick={() => onAsk(q)}
                disabled={disabled}
                className={cn(
                  "!rounded-full !border !px-2.5 !py-1 !text-[11px] !transition-colors disabled:!opacity-50",
                  isDark
                    ? "!border-slate-600 !text-slate-300 hover:!bg-slate-800"
                    : "!border-slate-300 !text-slate-600 hover:!bg-slate-100 dark:!border-slate-600 dark:!text-slate-300 dark:hover:!bg-slate-800"
                )}
              >
                {q}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

/** Source citations under a chat answer — file + page, with download. */
function SourceRefs({ refs, isDark }: { refs: SourceReference[]; isDark: boolean }) {
  const [dl, setDl] = useState<number | null>(null)
  const [err, setErr] = useState("")

  async function handleDownload(r: SourceReference) {
    if (!r.document_id || dl !== null) return
    setDl(r.document_id)
    setErr("")
    try {
      await downloadPolicyDocument(r.document_id, r.document_name)
    } catch {
      setErr("Could not download the source file.")
    } finally {
      setDl(null)
    }
  }

  return (
    <div
      className={cn(
        "!mt-3 !pt-2 !border-t !space-y-1.5",
        isDark ? "!border-slate-600" : "!border-slate-300 dark:!border-slate-600"
      )}
    >
      <div
        className={cn(
          "!text-[11px] !font-medium !uppercase !tracking-wide",
          isDark ? "!text-slate-400" : "!text-slate-500 dark:!text-slate-400"
        )}
      >
        Sources
      </div>
      <div className="!flex !flex-wrap !gap-1.5">
        {refs.map((r, i) =>
          r.document_id ? (
            <button
              key={i}
              type="button"
              onClick={() => handleDownload(r)}
              disabled={dl !== null}
              className={cn(
                "!inline-flex !items-center !gap-1.5 !rounded-md !border !px-2 !py-1 !text-[11px] !font-medium !transition-colors disabled:!opacity-50",
                isDark
                  ? "!border-blue-800 !text-blue-300 hover:!bg-blue-950"
                  : "!border-blue-200 !text-blue-600 hover:!bg-blue-50 dark:!border-blue-800 dark:!text-blue-300 dark:hover:!bg-blue-950"
              )}
              title={r.policy_reference || r.document_name}
            >
              {dl === r.document_id ? (
                <Loader2 className="!h-3 !w-3 !animate-spin" />
              ) : (
                <Download className="!h-3 !w-3" />
              )}
              {r.document_name || r.policy_reference || r.policy}
              {r.page && ` · p. ${r.page}`}
            </button>
          ) : (
            <span
              key={i}
              className={cn(
                "!text-[11px] !px-2 !py-1",
                isDark ? "!text-slate-400" : "!text-slate-500 dark:!text-slate-400"
              )}
            >
              {r.policy_reference || r.policy}
            </span>
          )
        )}
      </div>
      {err && <div className="!text-[11px] !text-red-500">{err}</div>}
    </div>
  )
}

export default function PolicySearchPage() {
  const { theme } = useTheme()

  const [messages, setMessages] = useState<ChatBubble[]>([])
  const [input, setInput] = useState("")
  const [thinking, setThinking] = useState(false)
  const [thinkingText, setThinkingText] = useState("")
  const [streaming, setStreaming] = useState(false)

  // Session state
  const [sessions, setSessions] = useState<ChatSession[]>([])
  const [activeSessionId, setActiveSessionId] = useState<number | null>(null)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [loadingSessions, setLoadingSessions] = useState(false)

  // Open sidebar by default on desktop
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)")
    setSidebarOpen(mq.matches)
    const handler = (e: MediaQueryListEvent) => setSidebarOpen(e.matches)
    mq.addEventListener("change", handler)
    return () => mq.removeEventListener("change", handler)
  }, [])

  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const stickToBottom = useRef(true)
  const [checking, setChecking] = useState(false)

  const isDark = theme === "dark"

  // Load session list
  const refreshSessions = useCallback(async () => {
    setLoadingSessions(true)
    try {
      const data = await getChatSessions()
      setSessions(data)
    } catch {
      // ignore
    } finally {
      setLoadingSessions(false)
    }
  }, [])

  useEffect(() => {
    refreshSessions()
  }, [refreshSessions])

  // Load messages when active session changes
  useEffect(() => {
    if (activeSessionId === null) {
      setMessages([])
      return
    }
    getSessionMessages(activeSessionId)
      .then((msgs) => {
        if (msgs && msgs.length) {
          setMessages(msgs.map((m) => ({ role: m.role, content: m.content })))
        } else {
          setMessages([])
        }
      })
      .catch(() => setMessages([]))
  }, [activeSessionId])

  function handleScroll() {
    const el = scrollRef.current
    if (!el) return
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    stickToBottom.current = distanceFromBottom < 80
  }

  useEffect(() => {
    if (scrollRef.current && stickToBottom.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages, thinking, thinkingText])

  async function handleNewChat() {
    if (streaming) return
    setActiveSessionId(null)
    setMessages([])
    setInput("")
    inputRef.current?.focus()
    refreshSessions()
  }

  async function handleSelectSession(sessionId: number) {
    if (streaming) return
    setActiveSessionId(sessionId)
    // Close sidebar on mobile after selecting
    if (window.innerWidth < 768) setSidebarOpen(false)
  }

  async function handleDeleteSession(e: React.MouseEvent, sessionId: number) {
    e.stopPropagation()
    if (streaming) return
    try {
      await deleteChatSession(sessionId)
      if (activeSessionId === sessionId) {
        setActiveSessionId(null)
        setMessages([])
      }
      refreshSessions()
    } catch {
      // ignore
    }
  }

  async function send(e?: React.FormEvent, overrideText?: string) {
    e?.preventDefault()
    const text = (overrideText ?? input).trim()
    if (!text || streaming) return

    const userMsg: ChatBubble = { role: "user", content: text }
    setMessages((m) => [...m, userMsg])
    setInput("")
    setThinking(true)
    setThinkingText("Thinking...")
    setStreaming(true)
    stickToBottom.current = true

    let assistant = ""
    let refs: SourceReference[] = []

    try {
      await streamPolicyChat(text, (ev: ChatStreamEvent) => {
        if (ev.event === "thinking") {
          setThinkingText(ev.data || "Thinking...")
        } else if (ev.event === "references") {
          try {
            refs = JSON.parse(ev.data)
          } catch {
            refs = []
          }
        } else if (ev.event === "token") {
          setThinking(false)
          assistant += ev.data
          setMessages((m) => {
            const copy = [...m]
            const last = copy[copy.length - 1]
            if (last && last.role === "assistant" && last.streaming) {
              copy[copy.length - 1] = { ...last, content: assistant, references: refs }
            } else {
              copy.push({ role: "assistant", content: assistant, streaming: true, references: refs })
            }
            return copy
          })
        } else if (ev.event === "done") {
          setThinking(false)
          setStreaming(false)
          setMessages((m) => {
            const copy = [...m]
            const last = copy[copy.length - 1]
            if (last && last.role === "assistant") {
              copy[copy.length - 1] = { ...last, streaming: false, references: refs.length ? refs : last.references }
            }
            return copy
          })
          // Parse session_id from done event
          try {
            const doneData = JSON.parse(ev.data)
            if (doneData.session_id) {
              setActiveSessionId(doneData.session_id)
              refreshSessions()
            }
          } catch {
            // ignore
          }
        } else if (ev.event === "error") {
          setThinking(false)
          setStreaming(false)
          setMessages((m) => [
            ...m,
            { role: "assistant", content: `⚠️ ${ev.data}`, streaming: false },
          ])
        }
      }, activeSessionId ?? undefined)
    } catch (err: any) {
      setThinking(false)
      setStreaming(false)
      setMessages((m) => [
        ...m,
        { role: "assistant", content: `⚠️ ${err?.message || "Chat stream failed"}`, streaming: false },
      ])
    } finally {
      inputRef.current?.focus()
    }
  }

  function ask(q: string) {
    if (streaming || checking) return
    void send(undefined, q)
  }

  async function handleFilePick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (!file || streaming || checking) return

    setMessages((m) => [
      ...m,
      { role: "user", content: `📎 Check compliance: ${file.name}` },
    ])
    setChecking(true)
    setThinking(true)
    setThinkingText("Checking against policies…")
    stickToBottom.current = true

    try {
      const result = await checkCompliance(file)
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "", compliance: result },
      ])
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail || err?.message || "Compliance check failed"
      setMessages((m) => [
        ...m,
        { role: "assistant", content: `⚠️ ${detail}` },
      ])
    } finally {
      setChecking(false)
      setThinking(false)
      inputRef.current?.focus()
    }
  }

  return (
    <DashboardLayout>
      <div className="!flex !h-[calc(100vh-3.5rem)] !w-full !p-3 md:!p-6 !gap-0 md:!gap-4 !relative">
        {/* ─── Mobile Backdrop ─── */}
        <AnimatePresence>
          {sidebarOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="md:!hidden !fixed !inset-0 !z-40 !bg-black/50"
              onClick={() => setSidebarOpen(false)}
            />
          )}
        </AnimatePresence>

        {/* ─── Sidebar: Sessions ─── */}
        <AnimatePresence initial={false}>
          {sidebarOpen && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 280, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              className="!overflow-hidden !flex-shrink-0 !z-50 md:!z-auto !absolute md:!relative !top-0 !left-0 !h-full md:!h-auto"
            >
              <Card
                className={cn(
                  "!h-full !flex !flex-col !gap-0 !py-0 !overflow-hidden !w-[280px] !max-w-[85vw]",
                  isDark
                    ? "!bg-slate-900 !border-slate-800"
                    : "!bg-white/80 dark:!bg-slate-900/80 !border-slate-200/60 dark:!border-slate-700/60"
                )}
              >
                {/* Sidebar Header */}
                <div
                  className={cn(
                    "!p-4 !flex !items-center !justify-between !border-b",
                    isDark ? "!border-slate-800" : "!border-slate-200/60 dark:!border-slate-700/60"
                  )}
                >
                  <span
                    className={cn(
                      "!text-sm !font-semibold",
                      isDark ? "!text-slate-100" : "!text-slate-700 dark:!text-slate-200"
                    )}
                  >
                    Chats
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="!h-7 !w-7"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <PanelLeftClose className="!h-4 !w-4" />
                  </Button>
                </div>

                {/* New Chat Button */}
                <div className="!p-3">
                  <Button
                    onClick={handleNewChat}
                    disabled={streaming}
                    className={cn(
                      "!w-full !justify-start !gap-2 !rounded-lg !font-medium",
                      isDark
                        ? "!bg-gradient-to-r !from-blue-600 !to-purple-600 !text-white hover:!from-blue-500 hover:!to-purple-500"
                        : "!bg-gradient-to-r !from-blue-500 !to-purple-600 !text-white hover:!from-blue-400 hover:!to-purple-500"
                    )}
                  >
                    <Plus className="!h-4 !w-4" />
                    New Chat
                  </Button>
                </div>

                {/* Session List */}
                <div className="!flex-1 !overflow-y-auto !px-2 !pb-2 !space-y-1">
                  {loadingSessions && (
                    <div className="!flex !items-center !justify-center !py-8">
                      <Loader2 className="!h-5 !w-5 !animate-spin !text-slate-400" />
                    </div>
                  )}
                  {!loadingSessions && sessions.length === 0 && (
                    <div
                      className={cn(
                        "!text-center !py-8 !text-xs",
                        isDark ? "!text-slate-500" : "!text-slate-400"
                      )}
                    >
                      No previous chats yet
                    </div>
                  )}
                  {sessions.map((s) => (
                    <div
                      key={s.id}
                      onClick={() => handleSelectSession(s.id)}
                      className={cn(
                        "group !flex !items-center !gap-2 !px-3 !py-2.5 !rounded-lg !cursor-pointer !transition-colors",
                        activeSessionId === s.id
                          ? isDark
                            ? "!bg-blue-950 !text-blue-300"
                            : "!bg-blue-50 dark:!bg-blue-900/30 !text-blue-700 dark:!text-blue-300"
                          : isDark
                          ? "!text-slate-300 hover:!bg-slate-800"
                          : "!text-slate-600 dark:!text-slate-400 hover:!bg-slate-100 dark:hover:!bg-slate-900"
                      )}
                    >
                      <MessageSquare
                        className={cn(
                          "!h-3.5 !w-3.5 !flex-shrink-0",
                          activeSessionId === s.id
                            ? "!text-blue-400"
                            : isDark
                            ? "!text-slate-500 dark:!text-slate-400"
                            : "!text-slate-400"
                        )}
                      />
                      <div className="!flex-1 !min-w-0">
                        <div className="!text-xs !font-medium !truncate">
                          {s.title}
                        </div>
                        <div
                          className={cn(
                            "!text-[10px] !truncate",
                            isDark ? "!text-slate-500" : "!text-slate-400"
                          )}
                        >
                          {s.message_count} message{s.message_count !== 1 ? "s" : ""}
                        </div>
                      </div>
                      <button
                        onClick={(e) => handleDeleteSession(e, s.id)}
                        className={cn(
                          "opacity-0 group-hover:opacity-100 !transition-opacity !flex-shrink-0",
                          isDark ? "!text-slate-500 hover:!text-red-400" : "!text-slate-400 hover:!text-red-500 dark:hover:!text-red-400"
                        )}
                      >
                        <Trash2 className="!h-3.5 !w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ─── Main Chat Area ─── */}
        <Card
          className={cn(
            "!flex-1 !flex !flex-col !gap-0 !py-0 !overflow-hidden",
            isDark
              ? "!bg-slate-900 !border-slate-800"
              : "!bg-white/80 dark:!bg-slate-900/80 !border-slate-200/60 dark:!border-slate-700/60"
          )}
        >
          <CardHeader
            className={cn(
              "!py-4 !flex-row !items-center !justify-between !border-b",
              isDark ? "!border-slate-800" : "!border-slate-200/60 dark:!border-slate-700/60"
            )}
          >
            <CardTitle className="!flex !items-center !gap-2 !text-base">
              {!sidebarOpen && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="!h-7 !w-7 !mr-1"
                  onClick={() => setSidebarOpen(true)}
                >
                  <PanelLeft className="!h-4 !w-4" />
                </Button>
              )}
              <Sparkles className="!h-4 !w-4 !text-blue-500 dark:!text-blue-400" />
              <span className={isDark ? "!text-slate-100" : "!text-slate-900 dark:!text-white"}>
                Policy Search Assistant
              </span>
            </CardTitle>
            <Badge
              variant="outline"
              className={cn(
                "!text-[10px]",
                isDark ? "!border-slate-700 !text-slate-400" : "!border-slate-300 dark:!border-slate-600 !text-slate-500 dark:!text-slate-400"
              )}
            >
              {messages.length} message{messages.length !== 1 ? "s" : ""}
            </Badge>
          </CardHeader>

          {/* Messages */}
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="!flex-1 !overflow-y-auto !p-3 md:!p-6 !space-y-4"
          >
            {messages.length === 0 && !thinking && (
              <div className="!flex !flex-col !items-center !justify-center !h-full !text-center !py-12">
                <div className="!w-16 !h-16 !rounded-full !bg-gradient-to-br !from-blue-500 !to-purple-600 !flex !items-center !justify-center !mb-4">
                  <MessageSquare className="!h-8 !w-8 !text-white" />
                </div>
                <h2
                  className={cn(
                    "!text-xl !font-bold !mb-2",
                    isDark ? "!text-white" : "!text-slate-900 dark:!text-white"
                  )}
                >
                  Policy Search Assistant
                </h2>
                <p
                  className={cn(
                    "!text-sm !max-w-md",
                    isDark ? "!text-slate-400" : "!text-slate-500 dark:!text-slate-400"
                  )}
                >
                  Ask any question and I'll help you find the answer. Or
                  upload a document with the 📎 button to check it against the
                  policies.
                </p>
                <div className="!flex !flex-wrap !gap-2 !justify-center !mt-4 !max-w-md">
                  {EMPTY_STATE_QUERIES.map((q) => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => ask(q)}
                      className={cn(
                        "!rounded-full !border !px-3 !py-1.5 !text-xs !transition-colors",
                        isDark
                          ? "!border-slate-600 !text-slate-300 hover:!bg-slate-800"
                          : "!border-slate-300 !text-slate-600 hover:!bg-slate-100 dark:!border-slate-600 dark:!text-slate-300 dark:hover:!bg-slate-800"
                      )}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <AnimatePresence initial={false}>
              {messages.map((m, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={cn(
                    "!flex !gap-2 md:!gap-3 !max-w-3xl !mx-auto",
                    m.role === "user" ? "!flex-row-reverse" : ""
                  )}
                >
                  <div
                    className={cn(
                      "!w-7 !h-7 md:!w-8 md:!h-8 !rounded-full !flex !items-center !justify-center !text-xs !font-bold !flex-shrink-0",
                      m.role === "user"
                        ? "!bg-gradient-to-br !from-blue-500 !to-purple-600 !text-white"
                        : isDark
                        ? "!bg-slate-700 !text-slate-300"
                        : "!bg-slate-200 dark:!bg-slate-800 !text-slate-600 dark:!text-slate-400"
                    )}
                  >
                    {m.role === "user" ? "You" : "AI"}
                  </div>
                  <div
                    className={cn(
                      "!rounded-2xl !px-3 md:!px-4 !py-2 md:!py-3",
                      m.compliance ? "!w-full !max-w-full" : "!max-w-[80%] !prose !prose-sm dark:!prose-invert !max-w-none",
                      m.role === "user"
                        ? "!bg-gradient-to-br !from-blue-500 !to-purple-600 !text-white !rounded-tr-sm"
                        : isDark
                        ? "!bg-slate-700/70 !text-slate-100 !rounded-tl-sm"
                        : "!bg-slate-100 dark:!bg-slate-900 !text-slate-900 dark:!text-white !rounded-tl-sm"
                    )}
                  >
                    {m.role === "user" ? (
                      <div className="!whitespace-pre-wrap !text-sm">{m.content}</div>
                    ) : m.compliance ? (
                      <ComplianceResultCard
                        r={m.compliance}
                        isDark={isDark}
                        disabled={streaming || checking}
                        onAsk={ask}
                      />
                    ) : (
                      <div className="policy-chat-markdown !text-sm !leading-relaxed">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            table: ({ children }) => (
                              <div className="!overflow-x-auto !my-3">
                                <table className="!min-w-full !border-collapse !text-xs">
                                  {children}
                                </table>
                              </div>
                            ),
                            th: ({ children }) => (
                              <th
                                className={cn(
                                  "!border !px-2.5 !py-1.5 !text-left !font-semibold",
                                  isDark
                                    ? "!border-slate-600 !bg-slate-800 !text-slate-200"
                                    : "!border-slate-300 !bg-slate-200/70 !text-slate-700 dark:!border-slate-600 dark:!bg-slate-800 dark:!text-slate-200"
                                )}
                              >
                                {children}
                              </th>
                            ),
                            td: ({ children }) => (
                              <td
                                className={cn(
                                  "!border !px-2.5 !py-1.5 !align-top",
                                  isDark
                                    ? "!border-slate-600"
                                    : "!border-slate-300 dark:!border-slate-600"
                                )}
                              >
                                {children}
                              </td>
                            ),
                          }}
                        >
                          {m.content}
                        </ReactMarkdown>
                        {m.references && m.references.length > 0 && (
                          <SourceRefs refs={m.references} isDark={isDark} />
                        )}
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {thinking && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="!flex !gap-2 md:!gap-3 !max-w-3xl !mx-auto"
              >
                <div
                  className={cn(
                    "!w-7 !h-7 md:!w-8 md:!h-8 !rounded-full !flex !items-center !justify-center !text-xs !font-bold !flex-shrink-0",
                    isDark ? "!bg-slate-700 !text-slate-300" : "!bg-slate-200 dark:!bg-slate-800 !text-slate-600 dark:!text-slate-400"
                  )}
                >
                  AI
                </div>
                <div
                  className={cn(
                    "!rounded-2xl !rounded-tl-sm !px-3 md:!px-4 !py-2 md:!py-3 !flex !items-center !gap-2",
                    isDark ? "!bg-slate-700/70" : "!bg-slate-100 dark:!bg-slate-900"
                  )}
                >
                  <span className="!flex !gap-1">
                    <span className="!w-2 !h-2 !rounded-full !bg-blue-500 !animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="!w-2 !h-2 !rounded-full !bg-blue-500 !animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="!w-2 !h-2 !rounded-full !bg-blue-500 !animate-bounce" style={{ animationDelay: "300ms" }} />
                  </span>
                  <span className={cn("!text-xs", isDark ? "!text-slate-400" : "!text-slate-500 dark:!text-slate-400")}>
                    {thinkingText}
                  </span>
                </div>
              </motion.div>
            )}
          </div>

          {/* Composer */}
          <form
            onSubmit={send}
            className={cn(
              "!p-3 md:!p-4 !border-t !flex !gap-2 !items-end",
              isDark ? "!border-slate-800" : "!border-slate-200/60 dark:!border-slate-700/60"
            )}
          >
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.xlsx,.xls,.docx,.doc"
              className="!hidden"
              onChange={handleFilePick}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              title="Upload a document to check it against the policies"
              className="!h-10 !w-10 !rounded-lg"
              disabled={streaming || checking}
              onClick={() => fileRef.current?.click()}
            >
              {checking ? (
                <Loader2 className="!h-4 !w-4 !animate-spin" />
              ) : (
                <Paperclip className="!h-4 !w-4" />
              )}
            </Button>
            <Textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault()
                  send()
                }
              }}
              placeholder="Ask anything about the policies…"
              rows={1}
              className={cn(
                "!flex-1 !resize-none !max-h-32 !min-h-10",
                isDark
                  ? "!bg-slate-700/50 !border-slate-600 !text-white !placeholder-slate-400"
                  : "!bg-white dark:!bg-slate-900 !border-slate-300 dark:!border-slate-600 !text-slate-900 dark:!text-white !placeholder-slate-400 dark:!placeholder-slate-500"
              )}
              disabled={streaming}
            />
            <Button
              type="submit"
              size="icon"
              className="!h-10 !w-10 !rounded-lg"
              disabled={streaming || !input.trim()}
            >
              {streaming ? <Loader2 className="!h-4 !w-4 !animate-spin" /> : <Send className="!h-4 !w-4" />}
            </Button>
          </form>
        </Card>
      </div>
    </DashboardLayout>
  )
}
